import Vue from "vue";
import Router from "vue-router";
import { store } from "@/store";

// Containers
const DefaultContainer = () => import("@/containers/DefaultContainer");

// Views
const Dashboard = () => import("@/views/Dashboard");

//Views - Admin
const ListUsers = () => import("@/views/admin/user/ListUsers");
const ListCompanies = () => import("@/views/admin/company/ListCompanies");
const ListBranches = () => import("@/views/admin/branch/ListBranches");
const ListDevices = () => import("@/views/admin/device/ListDevices");
const ListAds = () => import("@/views/ads/ListAds");
const ListSurveys = () => import("@/views/surveys/ListSurveys");
const ListRegions = () => import("@/views/admin/region/ListRegions");

const AddCompany = () => import("@/views/admin/company/AddCompany");
const AddUser = () => import("@/views/admin/user/AddUser");
const AddBranch = () => import("@/views/admin/branch/AddBranch");
const AddDevice = () => import("@/views/admin/device/AddDevice");
const AddAd = () => import("@/views/ads/AddAd");
const AddRegion = () => import("@/views/admin/region/AddRegion");

const UpdateCompany = () => import("@/views/admin/company/UpdateCompany");
const UpdateUser = () => import("@/views/admin/user/UpdateUser");
const UpdateBranch = () => import("@/views/admin/branch/UpdateBranch");
const UpdateDevice = () => import("@/views/admin/device/UpdateDevice");
const UpdateAd = () => import("@/views/ads/UpdateAd");
const UpdateRegion = () => import("@/views/admin/region/UpdateRegion");

const ManageSurvey = () => import("@/views/surveys/ManageSurvey");
const Answers = () => import("@/views/surveys/Answers");

const Charts = () => import("@/views/reporting/Charts");
const BranchReport = () => import("@/views/reporting/BranchReport");
const RegionReport = () => import("@/views/reporting/RegionReport");
const UserReport = () => import("@/views/reporting/UserReport");
const AdReport = () => import("@/views/reporting/AdReport");
const SurveyReport = () => import("@/views/reporting/SurveyReport");

//Views - Pages
const Login = () => import("@/views/pages/Login");
const Detection = () => import("@/views/pages/Detection");

Vue.use(Router);

const ifAuthenticated = (to, from, next) => {
  if (router.app.$localStorage.get("holly-fi-token")) {
    next();
    return;
  }
  next("/pages/login");
};

const router = new Router({
  mode: "hash", // https://router.vuejs.org/api/#mode
  linkActiveClass: "open active",
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: "/",
      redirect: "/dashboard",
      name: "Home",
      component: DefaultContainer,
      children: [
        {
          path: "dashboard",
          name: "Dashboard",
          component: Dashboard,
          beforeEnter: ifAuthenticated
        },
        {
          path: "ads",
          redirect: "/ads/list",
          name: "Ads",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "list",
              name: "All Ads",
              component: ListAds,
              beforeEnter: ifAuthenticated
            },
            {
              path: "add",
              name: "Add Ad",
              component: AddAd,
              beforeEnter: ifAuthenticated
            },
            {
              path: "update/:id",
              name: "Update Ad",
              component: UpdateAd,
              beforeEnter: ifAuthenticated
            }
          ]
        },
        {
          path: "surveys",
          redirect: "/surveys/list",
          name: "Surveys",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "list",
              name: "All Surveys",
              component: ListSurveys,
              beforeEnter: ifAuthenticated
            },
            {
              path: "manage/:id?",
              name: "ManageSurvey",
              meta: {
                label: "Manage Survey"
              },
              component: ManageSurvey,
              beforeEnter: ifAuthenticated
            },
            {
              path: "answers/:userId",
              name: "Answers",
              meta: {
                label: "Answers"
              },
              component: Answers,
              beforeEnter: ifAuthenticated
            }
          ]
        },
        {
          path: "admin",
          redirect: "/admin/users",
          name: "Admin",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "users",
              name: "Users",
              redirect: "/admin/users/list",
              beforeEnter: ifAuthenticated,
              component: {
                render(c) {
                  return c("router-view");
                }
              },
              children: [
                {
                  path: "list",
                  name: "All Users",
                  component: ListUsers,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "add",
                  name: "Add User",
                  component: AddUser,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "update/:id",
                  name: "Update User",
                  component: UpdateUser,
                  beforeEnter: ifAuthenticated
                }
              ]
            },
            {
              path: "companies",
              name: "Companies",
              redirect: "/admin/companies/list",
              beforeEnter: ifAuthenticated,
              component: {
                render(c) {
                  return c("router-view");
                }
              },
              children: [
                {
                  path: "list",
                  name: "All Companies",
                  component: ListCompanies,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "add",
                  name: "Add Company",
                  component: AddCompany,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "update/:id",
                  name: "Update Company",
                  component: UpdateCompany,
                  beforeEnter: ifAuthenticated
                }
              ]
            },
            {
              path: "branches",
              name: "Branches",
              redirect: "/admin/branches/list",
              beforeEnter: ifAuthenticated,
              component: {
                render(c) {
                  return c("router-view");
                }
              },
              children: [
                {
                  path: "list",
                  name: "All Branches",
                  component: ListBranches,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "add",
                  name: "Add Branch",
                  component: AddBranch,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "update/:id",
                  name: "Update Branch",
                  component: UpdateBranch,
                  beforeEnter: ifAuthenticated
                }
              ]
            },
            {
              path: "regions",
              name: "Regions",
              redirect: "/admin/regions/list",
              beforeEnter: ifAuthenticated,
              component: {
                render(c) {
                  return c("router-view");
                }
              },
              children: [
                {
                  path: "list",
                  name: "All Regions",
                  component: ListRegions,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "add",
                  name: "Add Region",
                  component: AddRegion,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "update/:id",
                  name: "Update Region",
                  component: UpdateRegion,
                  beforeEnter: ifAuthenticated
                }
              ]
            },
            {
              path: "devices",
              name: "Devices",
              redirect: "/admin/devices/list",
              beforeEnter: ifAuthenticated,
              component: {
                render(c) {
                  return c("router-view");
                }
              },
              children: [
                {
                  path: "list",
                  name: "All Devices",
                  component: ListDevices,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "add",
                  name: "Add Device",
                  component: AddDevice,
                  beforeEnter: ifAuthenticated
                },
                {
                  path: "update/:id",
                  name: "Update Device",
                  component: UpdateDevice,
                  beforeEnter: ifAuthenticated
                }
              ]
            }
          ]
        },
        {
          path: "reporting",
          redirect: "/reporting/overall",
          name: "Reporting",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "branch-report",
              name: "Branch Report",
              component: BranchReport,
              beforeEnter: ifAuthenticated
            },
            {
              path: "region-report",
              name: "Region Report",
              component: RegionReport,
              beforeEnter: ifAuthenticated
            },
            {
              path: "user-report",
              name: "User Report",
              component: UserReport,
              beforeEnter: ifAuthenticated
            },
            {
              path: "charts",
              name: "Charts",
              component: Charts,
              beforeEnter: ifAuthenticated
            },
            {
              path: "ad-report",
              name: "AdReport",
              component: AdReport,
              beforeEnter: ifAuthenticated
            },
            {
              path: "survey-report",
              name: "SurveyReport",
              component: SurveyReport,
              beforeEnter: ifAuthenticated
            }
          ]
        }
      ]
    },
    {
      path: "/pages",
      redirect: "/pages/404",
      name: "Pages",
      component: {
        render(c) {
          return c("router-view");
        }
      },
      children: [
        {
          path: "login",
          name: "Login",
          component: Login
        },
        {
          path: "detection",
          name: "Detection",
          component: Detection
        }
      ]
    }
  ]
});

router.beforeEach((to, from, next) => {
  if (
    store.getters.apiToken === null &&
    Vue.localStorage.get("holly-fi-token")
  ) {
    store.commit("updateApiToken", Vue.localStorage.get("holly-fi-token"));
  }
  if (
    store.getters.currentUser === null &&
    Vue.localStorage.get("holly-fi-user")
  ) {
    store.commit(
      "updateCurrentUser",
      JSON.parse(Vue.localStorage.get("holly-fi-user"))
    );
  }
  if (from.name === "ManageSurvey") {
    store.commit("clearManageSurvey");
  }
  next();
});

export default router;
